// Deployment marker: 2025-08-12 v8
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { PLATFORM_BRAND } from '@/lib/platformBrand';
import { FileText, PenTool, CheckCircle } from 'lucide-react';
import SignContractPageV2 from '@/components/contracts/signing/SignContractPageV2';
import { contractService } from '@/services/contractService';
import { publicContractDocumentUrl } from '@/lib/publicContractDocumentUrl';
import type { SigningContextV2 } from '@/types/contracts';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://admin-api.joulescrm.com';

// CB-1.6: legacy finish-out ceremony — the entire pre-existing SignContractPage body,
// preserved byte-for-byte. Reached via the dispatcher below for legacy contracts.
function SignContractPageLegacy() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [step, setStep] = useState('loading');
  const [contract, setContract] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [signature, setSignature] = useState('');
  const [isSigning, setIsSigning] = useState(false);

  console.log('SignContractPage loaded, version: 2025-08-12 v7, token:', token);

  useEffect(() => {
    async function fetchContract() {
      if (!token) {
        setError('No token provided');
        setStep('error');
        return;
      }
      console.log('Fetching contract with token:', token);
      // SUB-1.12: was a direct anon SELECT on `contracts`. The `.eq('signature_token',
      // token)` filter here was client-supplied and therefore not a control: the
      // policy behind it only required that a token existed. The RPC matches the
      // token server-side and returns the same field set, plus contract_model.
      const { data, error } = await supabase.rpc('get_legacy_signing_contract', {
        p_token: token,
      });

      if (error || !data || data.status !== 'ok' || !data.contract) {
        console.error('Error fetching contract:', error);
        setError('Invalid or expired link');
        setStep('error');
      } else {
        console.log('Contract fetched successfully');
        setContract(data.contract);
        setStep('welcome');
      }
    }
    fetchContract();
  }, [token]);

  // Helper function to get the display name based on contract type
  const getContractTypeName = (type: string | null) => {
    if (!type) return 'Contract';
    switch (type) {
      case 'LOI':
        return 'Letter of Intent';
      case 'TurnKey':
        return 'Solar Installation Agreement';
      case 'MNDA':
        return 'Mutual Non-Disclosure Agreement';
      default:
        return 'Contract';
    }
  };

  // Determine the current signer's name and email
  const getCurrentSigner = () => {
    if (!contract) return { name: '', email: '' };
    console.log('Determining current signer for contract type:', contract.type, 'current_signer:', contract.current_signer);
    switch (contract.current_signer) {
      case 'signer1':
        return { name: contract.signer1_name || '', email: contract.signer1_email || '' };
      case 'signer2':
        return { name: contract.signer2_name || '', email: contract.signer2_email || '' };
      case 'signer3':
        return { name: contract.signer3_name || '', email: contract.signer3_email || '' };
      default:
        return { name: contract.customer_name || '', email: contract.customer_email || '' };
    }
  };

  const handleProceed = () => {
    console.log('Proceeding to signing step');
    setStep('signing');
  };

  const handleSign = async () => {
    if (!signature.trim()) {
      alert('Please enter your full name');
      return;
    }
    if (isSigning) return;
    setIsSigning(true);
    try {
      console.log('Starting signing process for signer:', contract.current_signer, 'with signature:', signature, 'contract type:', contract.type, 'contract id:', contract.id);
      const formData = contract.form_data || {};
      const signatureField = ['LOI', 'MNDA', 'TurnKey', 'Solar Installation Agreement'].includes(contract.type) ? 'Customer Signature' : `signature ${contract.current_signer === 'signer1' ? 1 : contract.current_signer === 'signer2' ? 2 : 3}`;

      // Format today's date for signature date field
      const today = new Date();
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const signatureDate = `${monthNames[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;

      const updatedData = {
        ...formData,
        [signatureField]: signature,
        'Customer Signature Date': signatureDate,
        'Signature Date': signatureDate, // Alternative placeholder name
      };
      console.log('Updated form_data to send to edge function:', updatedData);
      console.log('Calling edge function to generate contract with payload:', {
        action: 'generateContract',
        templateId: contract.template_id,
        projectId: contract.project_id,
        contractId: contract.id,
        data: updatedData,
        signing: true
      });
      const functionsBaseUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 'https://cqynudrcrwlodsxuowbm.supabase.co/functions/v1';
      const response = await fetch(`${functionsBaseUrl}/contract-builder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'generateContract',
          templateId: contract.template_id,
          projectId: contract.project_id,
          data: updatedData,
          contractId: contract.id,
          signing: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Edge function error:', errorData);
        throw new Error(errorData.message || 'Failed to generate contract');
      }
      const { pdfUrl } = await response.json();
      console.log('Generated PDF URL:', pdfUrl);
      setPdfUrl(pdfUrl);

      // Log the 'signed' event BEFORE the signing RPC, and the ordering is
      // load-bearing (SUB-1.12).
      //
      // This was previously a direct INSERT into contract_events, placed after
      // signing, and it had NEVER WORKED: contract_events_insert requires
      // `tenant_id = get_current_user_tenant_id()`, which is NULL on this
      // unauthenticated page, so `tenant_id = NULL` evaluated to NULL and every
      // insert was refused. The call is fire-and-forget with only a console.error, so
      // no customer signature on a legacy contract has ever been recorded. FR61 and
      // NFR5 both depend on that row existing.
      //
      // It now goes through log_legacy_contract_signed, which verifies the token and
      // stamps tenant_id from the contract row rather than trusting the browser. That
      // is why it has to run FIRST: sign_contract_new ROTATES signature_token, so
      // after signing the token in hand no longer matches any row and the RPC would
      // return {status:'invalid'}.
      //
      // Residual, accepted deliberately: if the signing RPC below then fails, a
      // 'signed' event exists for a signature that did not complete. Rare, and
      // strictly better than the current state of never recording one at all. The
      // proper home for this is inside sign_contract / sign_contract_new, written
      // atomically with the signature, which is a follow-up rather than something to
      // bolt onto the signing functions at the same time as everything else here.
      const currentSigner = getCurrentSigner();
      await supabase
        .rpc('log_legacy_contract_signed', {
          p_token: token,
          p_signer_name: currentSigner.name || signature,
          p_signer_email: currentSigner.email,
        })
        .then(({ error }) => {
          if (error) console.error('Failed to log signed event:', error);
        });

      // Call the appropriate RPC based on contract type
      const rpcFunction = ['LOI', 'MNDA', 'TurnKey'].includes(contract.type) ? 'sign_contract' : 'sign_contract_new';
      console.log(`Calling RPC function: ${rpcFunction}`);
      const { error: rpcError } = await supabase.rpc(rpcFunction, {
        p_token: token,
        p_signature: signature,
        p_pdf_url: pdfUrl,
      });

      if (rpcError) {
        console.error('RPC error:', rpcFunction, rpcError);
        throw new Error(`Failed to sign contract: ${rpcError.message}`);
      }
      console.log('Contract signed successfully via RPC:', rpcFunction);

      // SUB-1.12: the next signer's token is deliberately NOT read here any more.
      //
      // This used to re-read contracts.signature_token by id after signing, to build
      // the next signer's link. Two things were wrong with that: it needed an anon
      // SELECT on `contracts` (the policy this story removes), and because
      // sign_contract_new rotates the token, the value being read was the NEXT
      // signer's credential, handed to the current signer's browser so it could put
      // it in an email.
      //
      // The backend now derives that link itself. /contracts/public/send-contract-email
      // already receives contract_id and runs as service_role, so it reads the current
      // token and assembles the URL server-side, mirroring send.py:207-219. This page
      // no longer sends signature_link at all, and any value it did send is ignored.

      // Send email to the next signer if applicable (for non-LOI/MNDA/TurnKey contracts)
      if (contract.current_signer && !['LOI', 'MNDA', 'TurnKey'].includes(contract.type)) {
        let nextSigner: string | null = null;
        let nextSignerEmail: string | null = null;
        if (contract.current_signer === 'signer1' && contract.signer2_email) {
          nextSigner = 'signer2';
          nextSignerEmail = contract.signer2_email;
        } else if (contract.current_signer === 'signer2' && contract.signer3_email) {
          nextSigner = 'signer3';
          nextSignerEmail = contract.signer3_email;
        }
        if (nextSigner && nextSignerEmail) {
          // No signature_link in this body any more: the endpoint builds it from the
          // contract's current token. See the note above the signing RPC.
          console.log(`Requesting signature link email for ${nextSigner}:`, nextSignerEmail);
          const emailResponse = await fetch(`${BACKEND_URL}/api/v1/contracts/public/send-contract-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'sendContractEmail',
              customer_email: nextSignerEmail,
              contract_id: contract.id,
              tenant_id: contract.tenant_id,
            }),
          });
          if (!emailResponse.ok) {
            const errorData = await emailResponse.json();
            console.error('Failed to send signature link email:', errorData);
            // Log the error but don't fail the signing process
            console.warn('Proceeding despite email failure for next signer');
          } else {
            console.log('Signature link email sent successfully to:', nextSignerEmail);
          }
        }
      }

      // Send download link email to the current signer
      console.log('Sending download link email to:', currentSigner.email, 'with pdfUrl:', pdfUrl);
      try {
        const emailResponse = await fetch(`${BACKEND_URL}/api/v1/contracts/public/send-contract-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'sendDownloadLink',
            customer_email: currentSigner.email,
            download_link: pdfUrl,
            contract_id: contract.id,
            tenant_id: contract.tenant_id,
          }),
        });
        if (!emailResponse.ok) {
          const errorData = await emailResponse.json();
          console.error('Failed to send download link email:', errorData);
          // Log the error but don't fail the signing process
          console.warn('Proceeding despite download link email failure');
        } else {
          console.log('Download link email sent successfully to:', currentSigner.email);
        }
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        console.warn('Proceeding despite download link email failure');
      }

      setStep('success');
    } catch (error: any) {
      console.error('Error during signing process:', error);
      setError(`Error signing contract: ${error.message || 'Unknown error'}`);
      setStep('error');
    } finally {
      setIsSigning(false);
    }
  };

  // CB-11.2: this page is ANONYMOUS — an emailed link, no Supabase session — so it
  // cannot sign for itself against the private `contracts` bucket. It asks the backend,
  // which authorises the signature_token against the contract row and returns a
  // service_role-signed URL.
  //
  // It used to render `signed_document_url` raw, which worked only because the bucket
  // was public; CB-11.1 wired nine other call sites and missed this one. There is no
  // fallback to the stored value on purpose: a fallback would keep working right up
  // until the flip and hide that this page had regressed.
  // NOTE: distinct from `pdfUrl` above, which is the NEWLY GENERATED PDF returned after
  // signing and used for the download link and the confirmation email. This one is the
  // document being shown for review before signing.
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerFailed, setViewerFailed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setViewerUrl(null);
    setViewerFailed(false);
    void publicContractDocumentUrl({ signatureToken: token }).then((resolved) => {
      if (cancelled) return;
      if (resolved) setViewerUrl(resolved);
      else setViewerFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [token, contract?.id]);

  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#BEA04D]"></div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <p className="text-red-600 text-2xl font-semibold tracking-wide animate-pulse">{error}</p>
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#E0E0E0] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl w-full transform transition-all hover:scale-105 duration-300">
          <div className="flex justify-center mb-6">
            <div className="bg-[#BEA04D] text-white rounded-full p-3 animate-pulse">
              <FileText className="h-8 w-8" />
            </div>
          </div>
          <h2 className="text-4xl font-extrabold text-center mb-4" style={{ color: '#BEA04D' }}>
            Sign Your {getContractTypeName(contract.type)}
          </h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            You’ve been invited by {PLATFORM_BRAND} to review and sign your {getContractTypeName(contract.type)}. Please proceed carefully.
          </p>
          <button
            onClick={handleProceed}
            className="w-full bg-gradient-to-r from-[#BEA04D] to-[#A88C3F] text-white py-4 px-8 rounded-xl font-semibold text-lg shadow-lg hover:from-[#A88C3F] hover:to-[#BEA04D] focus:ring-4 focus:ring-[#BEA04D] transition duration-300 transform hover:-translate-y-1"
          >
            Proceed to Signing
          </button>
        </div>
      </div>
    );
  }

  if (step === 'signing') {
    const currentSigner = getCurrentSigner();
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#E0E0E0] p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-5xl w-full border border-gray-200 grid gap-8">
          <h2 className="text-3xl font-bold text-center flex items-center justify-center" style={{ color: '#BEA04D' }}>
            <FileText className="h-8 w-8 mr-3" /> Sign Your {getContractTypeName(contract.type)}
          </h2>
          <div className="bg-gray-50 p-6 rounded-xl shadow-inner border border-gray-100">
            {viewerFailed ? (
              <div className="w-full h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-lg flex items-center justify-center text-center text-sm text-gray-500 px-4">
                This document couldn&apos;t be loaded right now. Please refresh, or ask the sender
                for a fresh link.
              </div>
            ) : !viewerUrl ? (
              <div className="w-full h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-[#BEA04D]" />
              </div>
            ) : (
              <iframe src={viewerUrl} className="w-full h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-lg" title="Contract PDF" />
            )}
          </div>
          <div className="space-y-6">
            <p className="text-sm text-gray-500 italic bg-gray-50 p-4 rounded-lg shadow-sm">
              By signing, you agree to the legally binding terms and conditions outlined above.
            </p>
            <div className="relative">
              <label htmlFor="signature" className="flex items-center text-lg font-medium text-gray-700 mb-2">
                <PenTool className="h-6 w-6 mr-2 text-[#BEA04D]" /> Please type your full name to sign ({currentSigner.name})
              </label>
              <input
                id="signature"
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="w-full p-4 border-b-2 border-gray-300 bg-gray-50 rounded-t-lg shadow-sm focus:border-[#BEA04D] focus:ring-0 transition-all duration-200 text-lg placeholder-gray-400"
                placeholder="Type your full name here"
              />
            </div>
            <button
              onClick={handleSign}
              disabled={isSigning}
              className={`w-full bg-gradient-to-r from-[#BEA04D] to-[#A88C3F] text-white py-4 rounded-xl font-semibold text-lg shadow-lg transition duration-300 transform hover:-translate-y-1 ${isSigning ? 'opacity-50 cursor-not-allowed' : 'hover:from-[#A88C3F] hover:to-[#BEA04D]'}`}
            >
              {isSigning ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                  Signing...
                </div>
              ) : (
                'Sign Contract'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    const handleDownload = () => {
      const a = document.createElement('a');
      a.href = pdfUrl || '';
      a.download = 'signed_contract.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl w-full transform transition-all hover:scale-105 duration-300">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-12 w-12 text-green-600 animate-bounce" />
          </div>
          <p className="text-2xl font-semibold text-green-700 text-center mb-4">
            {getContractTypeName(contract.type)} Signed Successfully!
          </p>
          <p className="text-gray-600 text-center mb-8">
            Your agreement has been recorded. Download your copy below.
          </p>
          <button
            onClick={handleDownload}
            className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-4 rounded-xl font-semibold text-lg shadow-lg hover:from-green-700 hover:to-teal-700 focus:ring-4 focus:ring-green-300 transition duration-300 transform hover:-translate-y-1"
          >
            Download Signed Contract
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// CB-1.6 (AD-11): dual-model dispatcher at the public /sign entry. Routes the link's token to the
// legacy ceremony or the v2 ceremony (CB-5.1 SignContractPageV2). The two models use DISJOINT token
// columns — legacy = contracts.signature_token (a contract-level token); v2 = the per-signer
// contract_signers.access_token (AD-6) — so detection probes legacy first (unchanged), then falls
// back to the v2 token-scoped loader RPC, which is the canonical v2 resolver.
export function SignContractPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [model, setModel] = useState<'loading' | 'legacy' | 'v2'>('loading');
  // When the v2 loader resolves, hand its context straight to the page to avoid a second RPC.
  const [v2Context, setV2Context] = useState<SigningContextV2 | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function resolveModel() {
      // No token: let the legacy page render its own "No token provided" error (unchanged UX).
      if (!token) {
        if (!cancelled) setModel('legacy');
        return;
      }
      // 1) Legacy contract-level signature_token probe. A v2 link's access_token never
      //    matches that column, so this resolves only genuine legacy links.
      //
      //    SUB-1.12: this used to SELECT `contracts` directly, which relied on
      //    contracts_select_anonymous_with_token: FOR SELECT TO anon
      //    USING (signature_token IS NOT NULL). That checks a token EXISTS, not that
      //    the caller has one, so dropping the .eq() filter returned every tokened
      //    contract including its signature_token. Now goes through a token-scoped
      //    RPC that matches the token server-side.
      const { data, error } = await supabase.rpc('get_legacy_signing_contract', {
        p_token: token,
      });
      if (cancelled) return;
      if (!error && data?.status === 'ok' && data.contract) {
        setModel(data.contract.contract_model === 'v2' ? 'v2' : 'legacy');
        return;
      }
      // 2) v2 per-signer access_token resolution (AD-6) via the token-scoped loader RPC. status:'ok'
      //    (incl. terminal envelopes — the v2 page renders the friendly message) → route to v2 and
      //    reuse the loaded context. Anything else → legacy renders its invalid/expired-link UX.
      const result = await contractService.getSigningContextV2(token);
      if (cancelled) return;
      if (result.success && result.data && result.data.status === 'ok') {
        setV2Context(result.data);
        setModel('v2');
        return;
      }
      setModel('legacy');
    }
    resolveModel();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (model === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#BEA04D]"></div>
      </div>
    );
  }

  if (model === 'v2') {
    return <SignContractPageV2 token={token ?? ''} initialContext={v2Context} />;
  }

  return <SignContractPageLegacy />;
}